import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { Calculator, Send, CheckCircle2 } from "lucide-react";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const initial = {
  name: "",
  address: "",
  phone: "",
  email: "",
  service: "pressure_washing",
  knows_sqm: "no",
  sqm: "",
  notes: "",
  website: "", // honeypot
};

export default function FreeQuote() {
  useDocumentMeta({
    title: "Free Quote | Pressure Washing & LED Lighting Rangiora - Shine and Line",
    description: "Get a free no-obligation quote for pressure washing or LED pathway lighting. Servicing Rangiora and surrounding Waimakariri / North Canterbury areas. $3/sqm transparent pricing.",
  });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const estimate = useMemo(() => {
    if (form.knows_sqm !== "yes") return null;
    const n = parseFloat(form.sqm);
    if (!isFinite(n) || n <= 0) return null;
    if (form.service === "led_lighting") return null;
    return (n * 3).toFixed(2);
  }, [form.knows_sqm, form.sqm, form.service]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.address.trim() || !form.phone.trim()) {
      toast.error("Please fill in name, address, and phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        address: form.address.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        knows_sqm: form.knows_sqm === "yes",
        sqm: form.knows_sqm === "yes" && form.sqm ? parseFloat(form.sqm) : null,
        notes: form.notes.trim() || null,
        website: form.website, // honeypot
      };
      const { data } = await api.post("/quotes", payload);
      setSubmitted(data);
      toast.success("Quote request sent! We'll be in touch.");
      setForm(initial);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your request. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="quote-success">
        <Card className="rounded-3xl border-gray-100 shadow-sm">
          <CardContent className="p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-[#0369A1] mx-auto" />
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#111827] mt-4">Thanks, {submitted.name?.split(" ")[0]}!</h1>
            <p className="mt-3 text-[#4B5563]">We&apos;ve received your quote request for <strong>{submitted.service.replace("_", " ")}</strong>. Samuel will be in touch within one business day.</p>
            {submitted.estimated_price ? (
              <div className="mt-6 inline-block rounded-2xl bg-[#E0F2FE] text-[#0369A1] px-5 py-3 font-heading text-xl font-bold">
                Estimated: NZ${submitted.estimated_price.toFixed(2)}
              </div>
            ) : (
              <p className="mt-6 text-sm text-[#4B5563]">We&apos;ll come measure up and confirm pricing.</p>
            )}
            <div className="mt-8 flex justify-center gap-3">
              <Button onClick={() => setSubmitted(null)} className="rounded-full bg-[#0369A1] hover:bg-[#0284C7]" data-testid="quote-submit-another">Submit another</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-14 sm:py-20 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-widest text-[#0369A1] font-semibold">Free quote</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#111827] mt-3 leading-[1.04]">
            Tell us about your <span className="glow-underline">place</span>.
          </h1>
          <p className="mt-5 text-[#4B5563] leading-relaxed">Got the square meterage? We&apos;ll estimate it on the spot. Not sure? Easy - we&apos;ll pop out and measure up for free.</p>
          <ul className="mt-6 space-y-2 text-sm text-[#4B5563]">
            <li>· Pressure washing: NZ$3 per sqm</li>
            <li>· LED lighting: tailored quote</li>
            <li>· Combined package: best value</li>
            <li>· Free callout in Rangiora — travel fee may apply for surrounding areas</li>
          </ul>
        </div>

        <Card className="lg:col-span-3 rounded-3xl border-gray-100 shadow-sm" data-testid="quote-form-card">
          <CardContent className="p-6 sm:p-8">
            <form onSubmit={submit} className="space-y-5" data-testid="quote-form">
              {/* Honeypot - hidden from real users via CSS, bots will fill it */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
                <label htmlFor="q-website">Website (leave empty)</label>
                <input id="q-website" type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={upd("website")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="q-name">Full name *</Label>
                  <Input id="q-name" data-testid="quote-input-name" value={form.name} onChange={upd("name")} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="q-phone">Phone *</Label>
                  <Input id="q-phone" data-testid="quote-input-phone" value={form.phone} onChange={upd("phone")} required inputMode="tel" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="q-email">Email (optional, for confirmation)</Label>
                <Input id="q-email" data-testid="quote-input-email" type="email" value={form.email} onChange={upd("email")} />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="q-address">Address *</Label>
                <Input id="q-address" data-testid="quote-input-address" value={form.address} onChange={upd("address")} placeholder="123 Main St, Rangiora" required />
              </div>

              <div className="space-y-1.5">
                <Label>Service</Label>
                <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                  <SelectTrigger data-testid="quote-select-service"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pressure_washing">Pressure Washing</SelectItem>
                    <SelectItem value="led_lighting">LED Lighting</SelectItem>
                    <SelectItem value="package_deal">Combined Package Deal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Do you know the square meterage?</Label>
                <RadioGroup value={form.knows_sqm} onValueChange={(v) => setForm((f) => ({ ...f, knows_sqm: v }))} className="grid grid-cols-2 gap-3">
                  <label htmlFor="sqm-yes" className={`cursor-pointer rounded-2xl border p-4 flex items-center gap-3 ${form.knows_sqm === "yes" ? "border-[#0369A1] bg-[#E0F2FE]" : "border-gray-200"}`}>
                    <RadioGroupItem id="sqm-yes" value="yes" data-testid="quote-radio-sqm-yes" />
                    <div>
                      <div className="text-sm font-medium">Yes, I know</div>
                      <div className="text-xs text-[#4B5563]">I&apos;ll enter the sqm</div>
                    </div>
                  </label>
                  <label htmlFor="sqm-no" className={`cursor-pointer rounded-2xl border p-4 flex items-center gap-3 ${form.knows_sqm === "no" ? "border-[#0369A1] bg-[#E0F2FE]" : "border-gray-200"}`}>
                    <RadioGroupItem id="sqm-no" value="no" data-testid="quote-radio-sqm-no" />
                    <div>
                      <div className="text-sm font-medium">Please measure up</div>
                      <div className="text-xs text-[#4B5563]">We&apos;ll come out free</div>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              {form.knows_sqm === "yes" && (
                <div className="space-y-1.5">
                  <Label htmlFor="q-sqm">Approx. square meters</Label>
                  <Input id="q-sqm" data-testid="quote-input-sqm" type="number" min="0" step="0.5" value={form.sqm} onChange={upd("sqm")} />
                  {estimate && (
                    <div className="mt-2 flex items-center gap-2 rounded-xl bg-[#E0F2FE] text-[#0369A1] px-3 py-2 text-sm" data-testid="quote-estimate">
                      <Calculator className="w-4 h-4" /> Estimated pressure-washing cost: <strong>NZ${estimate}</strong>
                    </div>
                  )}
                  {form.service === "led_lighting" && (
                    <div className="text-xs text-[#4B5563] mt-1">LED lighting is quoted per project - we&apos;ll confirm pricing after measure-up.</div>
                  )}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="q-notes">Anything else? (optional)</Label>
                <Textarea id="q-notes" data-testid="quote-input-notes" value={form.notes} onChange={upd("notes")} placeholder="Driveway + back patio, mossy patches, etc." rows={3} />
              </div>

              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#0369A1] hover:bg-[#0284C7] py-6 text-base" data-testid="quote-submit">
                <Send className="w-4 h-4 mr-2" /> {loading ? "Sending..." : "Send quote request"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
